import { ChangeEvent, FC, useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import InstructionsSent from "./InstructionsSent"
import LoginSupport from "@/components/LoginSupport"
import AuthLayout from "@/components/shared/AuthLayout"
import Button from "@/components/shared/Button"
import Link from "@/components/shared/Link"
import TextInput from "@/components/shared/TextInput"
import { HttpCodes } from "@/constants/globals"
import { PageRoutes } from "@/constants/routes"
import useStateHandler from "@/hooks/useStateHandler"
import { AppDispatch, RootState } from "@/store"
import { passwordRecovery } from "@/store/authSlice"
import { Variant } from "@/types/index"
import { getErrorMessage } from "@/utils/index"
import { isEmpty, isNull } from "@/utils/lodash"
import "./index.scss"

const PasswordRecovery: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const { passwordRecoveryLoading, passwordRecoveryError } = useSelector(
      ({ authReducer }: RootState) => authReducer
   )
   const [state, setState] = useStateHandler({ email: "" })
   const [error, setError] = useState(!isNull(passwordRecoveryError))
   const [isSuccess, setIsSuccess] = useState(false)
   const { email } = state
   const isFormValid = !passwordRecoveryLoading && !isEmpty(email)

   const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setState(e)
      if (error) {
         setError(false)
      }
   }

   const handleSend = useCallback(async () => {
      try {
         const result = await dispatch(passwordRecovery({ email })).unwrap()
         if (result.status === HttpCodes.OK) {
            setIsSuccess(true)
         }
      } catch (err) {
         console.error("Password recovery failed:", err)
      }
   }, [dispatch, email])

   if (isSuccess) {
      return <InstructionsSent />
   }

   return (
      <AuthLayout
         iconName="lock"
         title="Password recovery"
         instructions={
            <>
               <div className="password-recovery__instructions-description">
                  Please enter the email address that is connected to your
                  account, an email with recovery instructions will be sent.
               </div>
               <LoginSupport />
            </>
         }
      >
         <>
            <TextInput
               name="email"
               variant={Variant.LARGE}
               disabled={passwordRecoveryLoading}
               error={error}
               placeholder="Your email address"
               value={email}
               onChange={handleChange}
            />
            <div className="password-recovery__form-button-wrapper">
               <Link
                  to={PageRoutes.LOGIN}
                  className="password-recovery__link"
               >
                  Back to login screen
               </Link>
               <Button
                  className="password-recovery__form-button"
                  disabled={!isFormValid}
                  onClick={handleSend}
               >
                  Send
               </Button>
            </div>
            <div className="password-recovery__form-text">
               Forgotten your email address? <br /> Please contact your local
               administrator.
            </div>
            <div className="password-recovery__error">
               {passwordRecoveryError &&
                  getErrorMessage(
                     passwordRecoveryError,
                     "Incorrect information, please check your password and account name and try again"
                  )}
            </div>
         </>
      </AuthLayout>
   )
}

export default PasswordRecovery
